import { useState } from 'react'

import { Credenza, CredenzaTitle } from '@/components/ui/credenza'
import DynamicCredenzaContent from '@/components/ui/credenza-content'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { PATH_NAME } from '@/config'
import { SwapSetting } from '@/containers/home/swap-form/SwapFormHeader'
import { useIsMd } from '@/hooks/style/use-is-md'
import { CommonInfo } from '@/icons'
import { CommonFixedSwap } from '@/icons/icons/CommonFixedSwap'
import { CommonFixedSwapMobile } from '@/icons/icons/CommonFixedSwapMobile'
import { CommonTurboSwap } from '@/icons/icons/CommonTurboSwap'
import { CommonTurboSwapMobile } from '@/icons/icons/CommonTurboSwapMobile'
import { cn } from '@/lib/utils'
import { useShowConfirmTransactionScreen } from '@/store'

import { useTranslation } from 'react-i18next'
import { Link, Outlet, useLocation } from 'react-router-dom'

import { Button } from '../ui/button'
import RenderIf from '../ui/render-if'
import { Separator } from '../ui/separator'
import { Text } from '../ui/text'

enum MenuId {
  TURBO_SWAP = 'turbo',
  FIXED_SWAP = 'fixed',
}

const LinkMenu = [
  {
    label: 'Turbo Swap',
    path: [PATH_NAME.HOME, PATH_NAME.SWAP],
    id: MenuId.TURBO_SWAP,
  },
  {
    label: 'Fixed-Rate Swap',
    path: [PATH_NAME.FIXED_SWAP_VAULT],
    id: MenuId.FIXED_SWAP,
  },
]

interface SwapIntroductionProps {
  initTab: MenuId
  isOpen: boolean
  onClose: () => void
}

const SwapIntroduction = ({ initTab, isOpen, onClose }: SwapIntroductionProps) => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState(initTab)
  const isMd = useIsMd()
  return (
    <Credenza open={isOpen} onOpenChange={onClose}>
      <DynamicCredenzaContent
        style={{ maxHeight: 'unset' }}
        open={isOpen}
        className='py-6 max-w-lg md:max-w-[600px] md:w-[600px]  px-6'
      >
        <div className='flex flex-col gap-6 px-px'>
          <CredenzaTitle className='flex items-center text-size-22'>{t('title.SWAP_MODE_DESCRIPTION')}</CredenzaTitle>
        </div>
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as MenuId)} className='w-full mt-6'>
          <TabsList className='grid w-full grid-cols-2'>
            <TabsTrigger value={MenuId.TURBO_SWAP}>{t('title.TURBO_SWAP')}</TabsTrigger>
            <TabsTrigger value={MenuId.FIXED_SWAP}>{t('title.FIXED_SWAP')}</TabsTrigger>
          </TabsList>

          <div
            className={cn('transition-all duration-300 ease-in-out h-[164px] mt-6', {
              'h-[200px] md:h-[248px]': activeTab === MenuId.FIXED_SWAP,
            })}
          >
            <RenderIf condition={activeTab === MenuId.TURBO_SWAP}>
              <Text size={16} className='md:mb-5'>
                {t('title.TURBO_SWAP_DESCRIPTION')}
              </Text>
              {isMd ? <CommonTurboSwap /> : <CommonTurboSwapMobile className='w-full' />}
            </RenderIf>
            <RenderIf condition={activeTab === MenuId.FIXED_SWAP}>
              <Text size={16} className='md:mb-5'>
                {t('title.FIXED_SWAP_DESCRIPTION')}
              </Text>
              {isMd ? <CommonFixedSwap /> : <CommonFixedSwapMobile className='w-full' />}
            </RenderIf>
          </div>
        </Tabs>
      </DynamicCredenzaContent>
    </Credenza>
  )
}

export const SwapLayout = () => {
  const { pathname } = useLocation()
  const [isOpenDescription, setIsOpenDescription] = useState(false)
  const showConfirmTrade = useShowConfirmTransactionScreen()

  return (
    <>
      <div
        className={cn('swap-container mx-auto ', {
          hidden: showConfirmTrade,
        })}
      >
        <div className={cn('flex justify-between mb-5')}>
          <div className='flex gap-6'>
            {LinkMenu.map((item) => {
              const isActive =
                item.id === MenuId.FIXED_SWAP ? pathname.includes(PATH_NAME.FIXED_SWAP) : item.path.includes(pathname)
              return (
                <Link
                  key={item.path[0]}
                  to={item.path[0]}
                  className={cn('text-white-48 text-size-18', isActive && 'text-white-100')}
                >
                  {item.label}
                </Link>
              )
            })}
          </div>
          <div className='flex gap-2'>
            <Button onClick={() => setIsOpenDescription(true)} variant='icon' size='sm' className='w-8'>
              <CommonInfo className='size-6' />
            </Button>
            <SwapSetting />
          </div>
          {isOpenDescription && (
            <SwapIntroduction
              isOpen={isOpenDescription}
              onClose={() => setIsOpenDescription(false)}
              initTab={pathname.includes(PATH_NAME.FIXED_SWAP) ? MenuId.FIXED_SWAP : MenuId.TURBO_SWAP}
            />
          )}
        </div>
        <Separator className='my-5' />
      </div>
      <Outlet />
    </>
  )
}
